"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useDataIngestion } from './DataIngestionContext';
import { useStrategy } from './StrategyContext';
import {
    FinancialProjectionModel,
    YearlyFinancials,
    FiscalYear,
    Scenario,
    CellMetadata,
    createEmptyProjectionModel,
    createDefaultScenario,
    createEmptyYearlyFinancials,
    createUserEditMetadata,
    createAIMetadata,
    parseFiscalYear,
    toFiscalYear,
    generateFiscalYearRange,
    InitiativeYearlyImpact
} from '@/lib/schemas/financialProjection';
import {
    recalculateModel,
    recalculateFromYear,
    updateCell as engineUpdateCell,
    addYear as engineAddYear,
    removeYear as engineRemoveYear,
    generateWaterfallData,
    WaterfallBar,
    CellPath
} from '@/lib/utils/financialCalcEngine';

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface FinancialProjectionContextType {
    // Model state
    model: FinancialProjectionModel | null;
    isLoading: boolean;
    isCalculating: boolean;
    error: string | null;
    
    // Active scenario data (convenience accessors)
    activeScenario: Scenario | null;
    projections: YearlyFinancials[];
    yearRange: FiscalYear[];
    
    // Actions
    initializeModel: (baseRevenue: number, targets: { short?: number; medium?: number; long?: number }) => void;
    updateCell: (year: FiscalYear, path: CellPath, value: number) => void;
    updateAssumption: (year: FiscalYear, field: 'organicGrowthRate' | 'ebitdaMarginPercent', value: number) => void;
    addYear: () => void;
    removeYear: () => void;
    setYearRange: (start: FiscalYear, end: FiscalYear) => void;
    
    // Scenario management
    createScenario: (name: string, description?: string) => void;
    switchScenario: (scenarioId: string) => void;
    deleteScenario: (scenarioId: string) => void;
    duplicateScenario: (scenarioId: string, newName: string) => void;
    
    // Initiative management
    updateInitiativeImpact: (initiativeId: string, year: FiscalYear, updates: Partial<InitiativeYearlyImpact>) => void;
    syncInitiativeAnalysis: (initiativeId: string, initiativeName: string, yearlyImpacts: Array<{ year: string; revenueImpact: number; ebitdaImpact: number; capex: number; opex: number }>) => void;
    
    // UI state
    collapsedYears: FiscalYear[];
    toggleYearCollapse: (year: FiscalYear) => void;
    expandedInitiatives: string[];
    toggleInitiativeExpand: (initiativeId: string) => void;
    
    // Waterfall data
    getWaterfallData: (metric: 'revenue' | 'ebitda', startYear?: FiscalYear, endYear?: FiscalYear) => WaterfallBar[];
    
    // Validation
    triggerSupervisorValidation: () => Promise<void>;
    supervisorValidationStatus: 'idle' | 'validating' | 'validated' | 'error';
    
    // Display settings
    displayStartYear: FiscalYear;
    displayEndYear: FiscalYear;
    setDisplayYearRange: (start: FiscalYear, end: FiscalYear) => void;
}

const FinancialProjectionContext = createContext<FinancialProjectionContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export const FinancialProjectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { activeCompany, user } = useAuth();
    const { formData, structuredFinancials, updateStructuredFinancials } = useDataIngestion();
    const { activeInitiatives, blueprintGaps } = useStrategy();
    
    // Core state
    const [model, setModel] = useState<FinancialProjectionModel | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isCalculating, setIsCalculating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // UI state
    const [collapsedYears, setCollapsedYears] = useState<FiscalYear[]>([]);
    const [expandedInitiatives, setExpandedInitiatives] = useState<string[]>([]);
    const [displayStartYear, setDisplayStartYear] = useState<FiscalYear>('FY25');
    const [displayEndYear, setDisplayEndYear] = useState<FiscalYear>('FY30');
    const [supervisorValidationStatus, setSupervisorValidationStatus] = useState<'idle' | 'validating' | 'validated' | 'error'>('idle');
    
    // Track whether we've loaded from persistent storage (to avoid re-init overwriting edits)
    const [hasLoadedFromDB, setHasLoadedFromDB] = useState(false);
    
    // Cloud blob sync state
    const [isCloudHydrated, setIsCloudHydrated] = useState(false);
    const cloudSyncTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    
    // ========================================================================
    // PERSISTENCE — IndexedDB (offline cache) + Cloud blob (authoritative)
    // ========================================================================
    
    // Load from IndexedDB, then hydrate from cloud (authoritative)
    useEffect(() => {
        if (!activeCompany?.id) return;
        
        let cancelled = false;
        setIsLoading(true);
        
        (async () => {
            try {
                // PRIORITY 1: IndexedDB
                const { getProjectionModel } = await import('@/lib/utils/fileStorage');
                const stored = await getProjectionModel(activeCompany.id);
                
                if (!cancelled && stored?.model) {
                    const parsed = stored.model as FinancialProjectionModel;
                    const expectedYears = generateFiscalYearRange(parsed.startYear, parsed.endYear);
                    const activeScen = parsed.scenarios.find(s => s.id === parsed.activeScenarioId);
                    const actualYears = activeScen?.projections?.map(p => p.year) || [];
                    const hasAllYears = expectedYears.every(fy => actualYears.includes(fy));
                    
                    if (hasAllYears && actualYears.length === expectedYears.length) {
                        console.log("📊 Loaded valid model from IndexedDB with", actualYears.length, "years (updated:", stored.updatedAt, ")");
                        setModel(parsed);
                        setDisplayStartYear(parsed.startYear);
                        setDisplayEndYear(parsed.endYear);
                        setCollapsedYears((parsed as any).collapsedYears || []);
                        setExpandedInitiatives((parsed as any).expandedInitiatives || []);
                        setHasLoadedFromDB(true);
                    }
                }
                
                // Cloud blob (authoritative — overrides IndexedDB if present)
                if (!cancelled) {
                    try {
                        const res = await fetch(
                            `/api/cloud/company-blobs?companyId=${encodeURIComponent(activeCompany.id)}&blobType=financial_projection`
                        );
                        if (res.ok) {
                            const json = await res.json();
                            if (json.success && json.payload) {
                                const cloudModel = json.payload as FinancialProjectionModel;
                                const expectedYears = generateFiscalYearRange(cloudModel.startYear, cloudModel.endYear);
                                const activeScen = cloudModel.scenarios.find(s => s.id === cloudModel.activeScenarioId);
                                const actualYears = activeScen?.projections?.map(p => p.year) || [];
                                const hasAllYears = expectedYears.every(fy => actualYears.includes(fy));
                                
                                if (hasAllYears && actualYears.length === expectedYears.length) {
                                    console.log("📊 Loaded valid model from cloud with", actualYears.length, "years");
                                    setModel(cloudModel);
                                    setDisplayStartYear(cloudModel.startYear);
                                    setDisplayEndYear(cloudModel.endYear);
                                    setCollapsedYears((cloudModel as any).collapsedYears || []);
                                    setExpandedInitiatives((cloudModel as any).expandedInitiatives || []);
                                    setHasLoadedFromDB(true);
                                }
                            }
                        }
                    } catch (cloudErr) {
                        console.warn("Cloud hydration failed (offline?) — IndexedDB data stands:", cloudErr);
                    }
                }
                
                if (!cancelled) setIsCloudHydrated(true);
            } catch (e) {
                console.error('Failed to load financial projection from DB:', e);
            } finally {
                if (!cancelled) {
                    setIsCloudHydrated(true);
                    setIsLoading(false);
                }
            }
        })();
        
        return () => { cancelled = true; };
    }, [activeCompany?.id]); // eslint-disable-line react-hooks/exhaustive-deps
    
    // Save to IndexedDB whenever model changes (only after cloud hydration to avoid clobbering)
    useEffect(() => {
        if (!activeCompany?.id || !model || !isCloudHydrated) return;
        
        const toSave = {
            ...model,
            collapsedYears,
            expandedInitiatives
        };
        
        (async () => {
            try {
                const { saveProjectionModel } = await import('@/lib/utils/fileStorage');
                await saveProjectionModel(activeCompany.id, toSave);
            } catch (e) {
                console.warn('IndexedDB save failed:', e);
            }
        })();
    }, [model, collapsedYears, expandedInitiatives, activeCompany?.id, isCloudHydrated]);
    
    // Sync model to cloud blob (debounced)
    useEffect(() => {
        if (!hasLoadedFromDB || !isCloudHydrated || !activeCompany?.id || !user?.id) return;
        
        if (cloudSyncTimeoutRef.current) clearTimeout(cloudSyncTimeoutRef.current);
        cloudSyncTimeoutRef.current = setTimeout(async () => {
            try {
                await fetch("/api/cloud/company-blobs", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        companyId: activeCompany.id,
                        blobType: "financial_projection",
                        payload: model,
                    }),
                });
            } catch (e) {
                console.warn("Failed to sync financial projection to cloud:", e);
            }
        }, 700);
        
        return () => {
            if (cloudSyncTimeoutRef.current) clearTimeout(cloudSyncTimeoutRef.current);
        };
    }, [model, hasLoadedFromDB, isCloudHydrated, activeCompany?.id, user?.id]);
    
    // ========================================================================
    // AUTO-INITIALIZE FROM WIZARD DATA (only if no persisted model)
    // ========================================================================
    
    useEffect(() => {
        // CRITICAL: Do NOT re-initialize if we already loaded a user-edited model from DB
        if (!activeCompany || model || isLoading || hasLoadedFromDB) return;
        
        const hasWizardData = formData?.foundation || structuredFinancials?.revenue;
        if (!hasWizardData) return;
        
        console.log("📊 [FinancialProjection] No persisted model found — auto-initializing from wizard data...");
        
        const currentYear = new Date().getFullYear();
        const currentFY = currentYear - 2000;
        
        let baseRevenue = 0;
        
        if (structuredFinancials?.revenue) {
            const revenueData = structuredFinancials.revenue;
            const actualYears = Object.keys(revenueData)
                .filter(key => {
                    const match = key.match(/FY(\d+)/);
                    if (!match) return key === 'LATEST';
                    return parseInt(match[1], 10) <= currentFY;
                })
                .sort((a, b) => {
                    if (a === 'LATEST') return 1;
                    if (b === 'LATEST') return -1;
                    return parseInt(b.replace('FY', ''), 10) - parseInt(a.replace('FY', ''), 10);
                });
            
            if (actualYears.length > 0) {
                baseRevenue = revenueData[actualYears[0]] || 0;
            }
        }
        
        if (baseRevenue === 0 && structuredFinancials?.revenue?.['LATEST']) {
            baseRevenue = structuredFinancials.revenue['LATEST'];
        }
        if (baseRevenue === 0) {
            const anyRevenue = Object.values(structuredFinancials?.revenue || {}).find(v => typeof v === 'number' && v > 0);
            if (anyRevenue) baseRevenue = anyRevenue;
        }
        // CRITICAL: If we still have 0 revenue, structuredFinancials hasn't hydrated yet.
        // Return early and let the next render cycle try again when data arrives.
        // Do NOT default to 10 — that creates a stale model that persists forever.
        if (baseRevenue === 0) {
            console.log("📊 [FinancialProjection] No revenue data available yet — deferring initialization");
            return;
        }
        
        const parseTarget = (val: string | number | undefined): number => {
            if (!val) return 0;
            if (typeof val === 'number') return val;
            const match = val.match(/([\d.]+)\s*([kKmMbB])?/);
            if (!match) return 0;
            let num = parseFloat(match[1]);
            const suffix = match[2]?.toLowerCase();
            if (suffix === 'k') num /= 1000;
            else if (suffix === 'b') num *= 1000;
            return num;
        };
        
        const targets = {
            short: parseTarget(formData?.foundation?.shortTermFinancial) || baseRevenue * 1.2,
            medium: parseTarget(formData?.foundation?.mediumTermFinancial) || baseRevenue * 1.5,
            long: parseTarget(formData?.foundation?.longTermFinancial) || baseRevenue * 2.5
        };
        
        console.log("📊 Auto-init with targets:", { baseRevenue, targets });
        initializeModel(baseRevenue, targets);
        
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCompany?.id, model, isLoading, hasLoadedFromDB, formData?.foundation, structuredFinancials?.revenue]);
    
    // ========================================================================
    // RE-SYNC: DISABLED — User edits are authoritative
    // ========================================================================
    // Previously this useEffect would watch structuredFinancials?.revenue and
    // re-initialize the entire model if the base revenue changed >10%.
    // This wiped all user edits on every page load because hydration timing 
    // caused spurious "changes". The persisted model is now the source of truth.
    // Users can re-initialize manually via the Validate button if needed.
    
    // ========================================================================
    // DERIVED STATE
    // ========================================================================
    
    const activeScenario = useMemo(() => {
        if (!model) return null;
        return model.scenarios.find(s => s.id === model.activeScenarioId) || null;
    }, [model]);
    
    const projections = useMemo(() => {
        if (!activeScenario) return [];
        return [...activeScenario.projections].sort((a, b) => 
            parseFiscalYear(a.year) - parseFiscalYear(b.year)
        );
    }, [activeScenario]);
    
    const yearRange = useMemo(() => {
        if (!model) return [];
        return generateFiscalYearRange(model.startYear, model.endYear);
    }, [model]);
    
    // ========================================================================
    // INITIALIZATION
    // ========================================================================
    
    const initializeModel = useCallback((
        baseRevenue: number,
        targets: { short?: number; medium?: number; long?: number }
    ) => {
        if (!activeCompany) return;
        
        const currency = activeCompany.reportingCurrency || 'USD';
        const reportingUnit = activeCompany.reportingUnit || 'M';
        
        // Create empty model
        const newModel = createEmptyProjectionModel(
            activeCompany.id,
            'FY25',
            'FY30',
            currency,
            reportingUnit as any
        );
        
        // Get the default scenario
        const scenario = newModel.scenarios[0];
        const years = generateFiscalYearRange('FY25', 'FY30');
        
        // Interpolate targets
        const shortTarget = targets.short || baseRevenue * 1.2;
        const mediumTarget = targets.medium || baseRevenue * 1.5;
        const longTarget = targets.long || baseRevenue * 2.5;
        
        // Build business plan targets by interpolating
        const targetsByYear: Record<string, number> = {
            'FY25': baseRevenue,
            'FY26': shortTarget,
            'FY27': mediumTarget,
            'FY28': mediumTarget + (longTarget - mediumTarget) * 0.33,
            'FY29': mediumTarget + (longTarget - mediumTarget) * 0.66,
            'FY30': longTarget
        };
        
        // ================================================================
        // AUTO-COMPUTE BASE YEAR ASSUMPTIONS FROM ACTUAL DATA
        // ================================================================
        
        // 1) Base year EBITDA margin = Actual EBITDA / Actual Revenue
        const actualEbitda = structuredFinancials?.ebitda?.['FY25']
            || structuredFinancials?.ebitda?.['LATEST']
            || Object.values(structuredFinancials?.ebitda || {}).find(v => typeof v === 'number' && v > 0)
            || 0;
        const baseEbitdaMargin = (baseRevenue > 0 && actualEbitda > 0)
            ? actualEbitda / baseRevenue
            : 0.20; // Reasonable default if no EBITDA data
        
        // 2) Base year organic growth = (FY25 Revenue − FY24 Revenue) / FY24 Revenue
        const priorYearRevenue = structuredFinancials?.revenue?.['FY24'] || 0;
        const baseOrganicGrowth = (priorYearRevenue > 0 && baseRevenue > 0)
            ? (baseRevenue - priorYearRevenue) / priorYearRevenue
            : 0.10; // Default 10% if no prior year data
        
        console.log("📊 [initializeModel] Derived base assumptions:", {
            actualEbitda,
            baseRevenue,
            baseEbitdaMargin: `${(baseEbitdaMargin * 100).toFixed(1)}%`,
            priorYearRevenue,
            baseOrganicGrowth: `${(baseOrganicGrowth * 100).toFixed(1)}%`
        });
        
        // ================================================================
        // PROGRESSIVE ASSUMPTIONS (AI-style logical progression)
        // ================================================================
        // Growth: starts from actual base growth, gradually increases toward
        // what's needed to achieve BP targets (but capped at reasonable levels).
        // Margin: starts from actual base margin, with gradual improvement
        // reflecting operating leverage and scale economies.
        // ================================================================
        
        // Calculate the implied CAGR needed to reach the long-term target
        const impliedCAGR = longTarget > 0 && baseRevenue > 0
            ? Math.pow(longTarget / baseRevenue, 1 / 5) - 1
            : baseOrganicGrowth;
        
        // Progressive growth: blend actual base growth toward implied CAGR
        // Year 1-2: closer to historical, Year 3-5: ramp toward CAGR
        const growthByYear: Record<string, number> = {};
        const marginByYear: Record<string, number> = {};
        
        years.forEach((year, index) => {
            if (index === 0) {
                // Base year: use actual computed values
                growthByYear[year] = baseOrganicGrowth;
                marginByYear[year] = baseEbitdaMargin;
            } else {
                // Progressive growth: weighted blend from base toward implied CAGR
                // More weight to historical in early years, ramping to target
                const rampFactor = index / (years.length - 1); // 0.2, 0.4, 0.6, 0.8, 1.0
                const blendedGrowth = baseOrganicGrowth + (impliedCAGR - baseOrganicGrowth) * rampFactor;
                // Cap between 2% and 40% (sanity bounds)
                growthByYear[year] = Math.max(0.02, Math.min(0.40, blendedGrowth));
                
                // Progressive margin: gradual improvement from base
                // Typical operating leverage: 50-100bps improvement per year
                const marginImprovement = index * 0.005; // 50bps per year
                const targetMargin = Math.min(baseEbitdaMargin + marginImprovement, 0.35); // Cap at 35%
                marginByYear[year] = Math.max(0.05, targetMargin); // Floor at 5%
            }
        });
        
        console.log("📊 [initializeModel] Progressive assumptions:", { growthByYear, marginByYear });
        
        // Update projections with real data
        scenario.projections = years.map((year, index) => {
            const yearData = createEmptyYearlyFinancials(year, index === 0);
            const margin = marginByYear[year];
            
            // Set business plan targets
            yearData.revenue.businessPlanTarget = targetsByYear[year] || 0;
            yearData.revenue.businessPlanTargetMetadata = createAIMetadata(
                index === 0 ? 100 : 80,
                index === 0 ? 'Actual' : 'Interpolated from wizard targets'
            );
            
            // For base year, set total = actual revenue
            if (index === 0) {
                yearData.revenue.total = baseRevenue;
                yearData.revenue.totalMetadata = { source: 'wizard_derived', confidence: 100 };
            }
            
            // Set assumptions — derived from actual data + progressive logic
            yearData.assumptions.organicGrowthRate = growthByYear[year];
            yearData.assumptions.organicGrowthRateMetadata = createAIMetadata(
                index === 0 ? 95 : 75,
                index === 0 ? 'Computed from actual YoY revenue growth' : 'Progressive blend toward BP target CAGR'
            );
            yearData.assumptions.ebitdaMarginPercent = margin;
            yearData.assumptions.ebitdaMarginPercentMetadata = createAIMetadata(
                index === 0 ? 95 : 75,
                index === 0 ? 'Computed from actual EBITDA / Revenue' : 'Progressive margin improvement (+50bps/yr)'
            );
            
            // Set EBITDA BP targets = Revenue BP Target × Margin
            yearData.ebitda.businessPlanTarget = (targetsByYear[year] || 0) * margin;
            yearData.ebitda.businessPlanTargetMetadata = createAIMetadata(
                index === 0 ? 95 : 75,
                'Revenue Target × EBITDA Margin'
            );
            
            // For base year, set EBITDA total from actual data
            if (index === 0) {
                yearData.ebitda.totalEbitda = actualEbitda > 0 ? actualEbitda : baseRevenue * margin;
                yearData.ebitda.totalEbitdaMetadata = {
                    source: actualEbitda > 0 ? 'wizard_derived' : 'calculated',
                    confidence: actualEbitda > 0 ? 100 : 70,
                    formula: actualEbitda > 0 ? 'From actual P&L data' : `${baseRevenue} × ${(margin * 100).toFixed(1)}%`
                };
            }
            
            return yearData;
        });
        
        newModel.scenarios[0] = scenario;
        
        // Recalculate all years
        const recalculated = recalculateModel(newModel);
        
        setModel(recalculated);
        setDisplayStartYear('FY25');
        setDisplayEndYear('FY30');
    }, [activeCompany, structuredFinancials]);
    
    // ========================================================================
    // CELL UPDATES
    // ========================================================================
    
    const updateCell = useCallback((year: FiscalYear, path: CellPath, value: number) => {
        if (!model) return;
        
        setIsCalculating(true);
        try {
            const updated = engineUpdateCell(model, year, path, value, user?.email);
            // Clear validation change indicators on user edit (they are transient)
            if (updated.lastValidationChanges && updated.lastValidationChanges.length > 0) {
                updated.lastValidationChanges = [];
            }
            setModel(updated);
        } finally {
            setIsCalculating(false);
        }
    }, [model, user?.email]);
    
    const updateAssumption = useCallback((
        year: FiscalYear,
        field: 'organicGrowthRate' | 'ebitdaMarginPercent',
        value: number
    ) => {
        updateCell(year, { section: 'assumptions', field }, value);
    }, [updateCell]);
    
    // ========================================================================
    // YEAR MANAGEMENT
    // ========================================================================
    
    const addYear = useCallback(() => {
        if (!model) return;
        
        setIsCalculating(true);
        try {
            const updated = engineAddYear(model);
            setModel(updated);
            setDisplayEndYear(updated.endYear);
        } finally {
            setIsCalculating(false);
        }
    }, [model]);
    
    const removeYear = useCallback(() => {
        if (!model) return;
        
        setIsCalculating(true);
        try {
            const updated = engineRemoveYear(model);
            setModel(updated);
            setDisplayEndYear(updated.endYear);
        } finally {
            setIsCalculating(false);
        }
    }, [model]);
    
    const setYearRange = useCallback((start: FiscalYear, end: FiscalYear) => {
        if (!model) return;
        
        setModel({
            ...model,
            startYear: start,
            endYear: end
        });
    }, [model]);
    
    // ========================================================================
    // SCENARIO MANAGEMENT
    // ========================================================================
    
    const createScenario = useCallback((name: string, description?: string) => {
        if (!model) return;
        
        const newScenario: Scenario = {
            id: `scenario-${Date.now()}`,
            name,
            description,
            isDefault: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: user?.email,
            projections: model.scenarios.find(s => s.id === model.activeScenarioId)?.projections || []
        };
        
        setModel({
            ...model,
            scenarios: [...model.scenarios, newScenario],
            activeScenarioId: newScenario.id
        });
    }, [model, user?.email]);
    
    const switchScenario = useCallback((scenarioId: string) => {
        if (!model) return;
        
        setModel({
            ...model,
            activeScenarioId: scenarioId
        });
    }, [model]);
    
    const deleteScenario = useCallback((scenarioId: string) => {
        if (!model) return;
        
        const scenario = model.scenarios.find(s => s.id === scenarioId);
        if (!scenario || scenario.isDefault) return; // Can't delete default
        
        const remaining = model.scenarios.filter(s => s.id !== scenarioId);
        
        setModel({
            ...model,
            scenarios: remaining,
            activeScenarioId: remaining[0]?.id || model.activeScenarioId
        });
    }, [model]);
    
    const duplicateScenario = useCallback((scenarioId: string, newName: string) => {
        if (!model) return;
        
        const source = model.scenarios.find(s => s.id === scenarioId);
        if (!source) return;
        
        const duplicate: Scenario = {
            ...source,
            id: `scenario-${Date.now()}`,
            name: newName,
            isDefault: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: user?.email,
            projections: JSON.parse(JSON.stringify(source.projections))
        };
        
        setModel({
            ...model,
            scenarios: [...model.scenarios, duplicate]
        });
    }, [model, user?.email]);
    
    // ========================================================================
    // INITIATIVE MANAGEMENT
    // ========================================================================
    
    const updateInitiativeImpact = useCallback((
        initiativeId: string,
        year: FiscalYear,
        updates: Partial<InitiativeYearlyImpact>
    ) => {
        if (!model) return;
        
        const activeScenario = model.scenarios.find(s => s.id === model.activeScenarioId);
        if (!activeScenario) return;
        
        const updatedProjections = activeScenario.projections.map(p => {
            if (p.year !== year) return p;
            
            const updatedRevenue = p.revenue.initiativeBreakdown.map(init => {
                if (init.initiativeId !== initiativeId) return init;
                return {
                    ...init,
                    ...updates,
                    revenueImpactMetadata: createUserEditMetadata(user?.email)
                };
            });
            
            const updatedEbitda = p.ebitda.initiativeBreakdown.map(init => {
                if (init.initiativeId !== initiativeId) return init;
                return {
                    ...init,
                    ...updates,
                    grossEbitdaImpactMetadata: createUserEditMetadata(user?.email)
                };
            });
            
            return {
                ...p,
                revenue: { ...p.revenue, initiativeBreakdown: updatedRevenue },
                ebitda: { ...p.ebitda, initiativeBreakdown: updatedEbitda }
            };
        });
        
        const updatedScenarios = model.scenarios.map(s => {
            if (s.id !== model.activeScenarioId) return s;
            return { ...s, projections: updatedProjections };
        });
        
        // Recalculate from the changed year
        const newModel = {
            ...model,
            scenarios: updatedScenarios
        };
        
        setModel(recalculateModel(newModel));
    }, [model, user?.email]);
    
    // Sync initiative analysis data to the projection model
    const syncInitiativeAnalysis = useCallback((
        initiativeId: string,
        initiativeName: string,
        yearlyImpacts: Array<{ year: string; revenueImpact: number; ebitdaImpact: number; capex: number; opex: number }>
    ) => {
        if (!model) {
            console.warn('[syncInitiativeAnalysis] No model loaded - cannot sync initiative');
            return;
        }
        
        console.log(`📊 [syncInitiativeAnalysis] Syncing initiative "${initiativeName}" with ${yearlyImpacts.length} yearly impacts`);
        
        const activeScenario = model.scenarios.find(s => s.id === model.activeScenarioId);
        if (!activeScenario) return;
        
        const updatedProjections = activeScenario.projections.map(p => {
            // Find the matching yearly impact for this projection year
            const yearImpact = yearlyImpacts.find(yi => yi.year === p.year);
            
            if (!yearImpact) return p;
            
            // Update or add initiative to revenue breakdown
            const existingRevenueIdx = p.revenue.initiativeBreakdown.findIndex(i => i.initiativeId === initiativeId);
            const impactMetadata = createUserEditMetadata(user?.email);
            const revenueInitiative: InitiativeYearlyImpact = {
                initiativeId,
                title: initiativeName,
                year: p.year,
                revenueImpact: yearImpact.revenueImpact,
                revenueImpactMetadata: impactMetadata,
                grossEbitdaImpact: yearImpact.ebitdaImpact,
                grossEbitdaImpactMetadata: impactMetadata,
                capex: yearImpact.capex,
                capexMetadata: impactMetadata,
                opex: yearImpact.opex,
                opexMetadata: impactMetadata,
                headcountAdded: 0,
                status: 'planned',
                rampUpPercentage: 100,
            };
            
            const newRevenueBreakdown = existingRevenueIdx >= 0
                ? p.revenue.initiativeBreakdown.map((i, idx) => idx === existingRevenueIdx ? revenueInitiative : i)
                : [...p.revenue.initiativeBreakdown, revenueInitiative];
            
            // Calculate new totals
            const newStrategyImpact = newRevenueBreakdown.reduce((sum, i) => sum + (i.revenueImpact || 0), 0);
            
            // Update EBITDA breakdown
            const existingEbitdaIdx = p.ebitda.initiativeBreakdown.findIndex(i => i.initiativeId === initiativeId);
            const ebitdaInitiative: InitiativeYearlyImpact = {
                initiativeId,
                title: initiativeName,
                year: p.year,
                revenueImpact: yearImpact.revenueImpact,
                revenueImpactMetadata: impactMetadata,
                grossEbitdaImpact: yearImpact.ebitdaImpact,
                grossEbitdaImpactMetadata: impactMetadata,
                capex: yearImpact.capex,
                capexMetadata: impactMetadata,
                opex: yearImpact.opex,
                opexMetadata: impactMetadata,
                headcountAdded: 0,
                status: 'planned',
                rampUpPercentage: 100,
            };
            
            const newEbitdaBreakdown = existingEbitdaIdx >= 0
                ? p.ebitda.initiativeBreakdown.map((i, idx) => idx === existingEbitdaIdx ? ebitdaInitiative : i)
                : [...p.ebitda.initiativeBreakdown, ebitdaInitiative];
            
            const newGrossStrategyImpact = newEbitdaBreakdown.reduce((sum, i) => sum + (i.grossEbitdaImpact || 0), 0);
            const newTotalCapex = newEbitdaBreakdown.reduce((sum, i) => sum + (i.capex || 0), 0);
            const newTotalOpex = newEbitdaBreakdown.reduce((sum, i) => sum + (i.opex || 0), 0);
            
            return {
                ...p,
                revenue: {
                    ...p.revenue,
                    strategyImpact: newStrategyImpact,
                    initiativeBreakdown: newRevenueBreakdown,
                    // Recalculate total
                    total: p.revenue.organicGrowth + newStrategyImpact + (p.isBaseYear ? p.revenue.businessPlanTarget : (p.revenue.businessPlanTarget - p.revenue.organicGrowth - newStrategyImpact > 0 ? p.revenue.businessPlanTarget - p.revenue.organicGrowth - newStrategyImpact : 0))
                },
                ebitda: {
                    ...p.ebitda,
                    grossStrategyImpact: newGrossStrategyImpact,
                    initiativeBreakdown: newEbitdaBreakdown,
                    totalCapex: newTotalCapex,
                    totalOpex: newTotalOpex,
                    netCashPosition: p.ebitda.totalEbitda - newTotalCapex - newTotalOpex
                }
            };
        });
        
        const updatedScenarios = model.scenarios.map(s => {
            if (s.id !== model.activeScenarioId) return s;
            return { ...s, projections: updatedProjections };
        });
        
        const newModel = {
            ...model,
            scenarios: updatedScenarios
        };
        
        console.log(`📊 [syncInitiativeAnalysis] Updated model with initiative "${initiativeName}"`);
        setModel(recalculateModel(newModel));
    }, [model, user?.email]);
    
    // ========================================================================
    // UI STATE
    // ========================================================================
    
    const toggleYearCollapse = useCallback((year: FiscalYear) => {
        setCollapsedYears(prev => 
            prev.includes(year) 
                ? prev.filter(y => y !== year)
                : [...prev, year]
        );
    }, []);
    
    const toggleInitiativeExpand = useCallback((initiativeId: string) => {
        setExpandedInitiatives(prev => 
            prev.includes(initiativeId)
                ? prev.filter(id => id !== initiativeId)
                : [...prev, initiativeId]
        );
    }, []);
    
    const setDisplayYearRange = useCallback((start: FiscalYear, end: FiscalYear) => {
        setDisplayStartYear(start);
        setDisplayEndYear(end);
    }, []);
    
    // ========================================================================
    // WATERFALL DATA
    // ========================================================================
    
    const getWaterfallData = useCallback((
        metric: 'revenue' | 'ebitda',
        startYear?: FiscalYear,
        endYear?: FiscalYear
    ): WaterfallBar[] => {
        if (!model) return [];
        return generateWaterfallData(
            model,
            metric,
            startYear || displayStartYear,
            endYear || displayEndYear
        );
    }, [model, displayStartYear, displayEndYear]);
    
    // ========================================================================
    // SUPERVISOR VALIDATION
    // ========================================================================
    
    const triggerSupervisorValidation = useCallback(async () => {
        if (!model || !activeCompany) return;
        
        setSupervisorValidationStatus('validating');
        
        const activeScen = model.scenarios.find(s => s.id === model.activeScenarioId);
        if (!activeScen) {
            setSupervisorValidationStatus('error');
            return;
        }
        
        // Track every value change for the UI change indicators
        const validationChanges: Array<{ year: string; field: string; oldValue: number; newValue: number; reason: string }> = [];
        
        // ====================================================================
        // STEP 1: Load authoritative financial data from IndexedDB
        //         (same 3-layer merge logic as the KPI Refresh button)
        // ====================================================================
        let mergedFinancials: Record<string, Record<string, number>> = {
            revenue: {},
            ebitda: {},
            netProfit: {}
        };
        
        try {
            const { getWizardData } = await import('@/lib/utils/fileStorage');
            const wizardData = await getWizardData(activeCompany.id);
            
            if (wizardData) {
                // Layer 1 (lowest priority): Current context values
                if (structuredFinancials?.revenue) mergedFinancials.revenue = { ...structuredFinancials.revenue };
                if (structuredFinancials?.ebitda) mergedFinancials.ebitda = { ...structuredFinancials.ebitda };
                if (structuredFinancials?.netProfit) mergedFinancials.netProfit = { ...structuredFinancials.netProfit };
                
                // Layer 2: IndexedDB structuredFinancials (overwrites context)
                if (wizardData.structuredFinancials) {
                    const sf = wizardData.structuredFinancials;
                    if (sf.revenue) {
                        for (const [k, v] of Object.entries(sf.revenue)) {
                            if (typeof v === 'number' && v !== 0) mergedFinancials.revenue[k] = v;
                        }
                    }
                    if (sf.ebitda) {
                        for (const [k, v] of Object.entries(sf.ebitda)) {
                            if (typeof v === 'number' && v !== 0) mergedFinancials.ebitda[k] = v;
                        }
                    }
                    if (sf.netProfit) {
                        for (const [k, v] of Object.entries(sf.netProfit)) {
                            if (typeof v === 'number' && v !== 0) mergedFinancials.netProfit[k] = v;
                        }
                    }
                }
                
                // Layer 3 (highest priority): P&L line items from wizard
                if (wizardData.formData?.detailedFinancials?.lineItems) {
                    const lineItems = wizardData.formData.detailedFinancials.lineItems;
                    const extractMetric = (ids: string[]): Record<string, number> => {
                        const result: Record<string, number> = {};
                        for (const id of ids) {
                            const item = lineItems.find((li: any) => li.id === id);
                            if (item?.values) {
                                for (const v of item.values) {
                                    if (v.value !== null && v.value !== 0) {
                                        result[v.year] = v.value;
                                    }
                                }
                            }
                        }
                        return result;
                    };
                    
                    const revFromPL = extractMetric(['revenue', 'total_revenue']);
                    const ebitdaFromPL = extractMetric(['ebitda']);
                    const netProfitFromPL = extractMetric(['net_profit', 'pat']);
                    
                    for (const [k, v] of Object.entries(revFromPL)) mergedFinancials.revenue[k] = v;
                    for (const [k, v] of Object.entries(ebitdaFromPL)) mergedFinancials.ebitda[k] = v;
                    for (const [k, v] of Object.entries(netProfitFromPL)) mergedFinancials.netProfit[k] = v;
                }
                
                console.log("✅ [Validate] Loaded authoritative financials from IndexedDB:", mergedFinancials);
            } else {
                console.warn("[Validate] No wizard data in IndexedDB — using current context values only");
                if (structuredFinancials?.revenue) mergedFinancials.revenue = { ...structuredFinancials.revenue };
                if (structuredFinancials?.ebitda) mergedFinancials.ebitda = { ...structuredFinancials.ebitda };
                if (structuredFinancials?.netProfit) mergedFinancials.netProfit = { ...structuredFinancials.netProfit };
            }
        } catch (e) {
            console.warn("[Validate] Failed to load IndexedDB data, falling back to context:", e);
            if (structuredFinancials?.revenue) mergedFinancials.revenue = { ...structuredFinancials.revenue };
            if (structuredFinancials?.ebitda) mergedFinancials.ebitda = { ...structuredFinancials.ebitda };
            if (structuredFinancials?.netProfit) mergedFinancials.netProfit = { ...structuredFinancials.netProfit };
        }
        
        // ====================================================================
        // STEP 2: Resolve the best base year revenue & EBITDA from merged data
        // ====================================================================
        const currentYear = new Date().getFullYear();
        const currentFY = currentYear - 2000; // e.g., 25 for 2025
        const baseYearKey = model.baseYear; // e.g., "FY25"
        
        // Find the best revenue value for the base year
        const resolveBaseValue = (data: Record<string, number>, fallbackKey?: string): number => {
            // Try exact base year key first (e.g. "FY25")
            if (data[baseYearKey] && data[baseYearKey] !== 0) return data[baseYearKey];
            // Try 4-digit year format (e.g. "2025")
            if (data[String(2000 + currentFY)] && data[String(2000 + currentFY)] !== 0) return data[String(2000 + currentFY)];
            // Try "LATEST"
            if (data['LATEST'] && data['LATEST'] !== 0) return data['LATEST'];
            // Try most recent available year <= current FY
            const sortedKeys = Object.keys(data)
                .filter(k => k.match(/FY\d+/) || k.match(/^\d{4}$/))
                .sort((a, b) => {
                    const numA = parseInt(a.replace('FY', ''), 10);
                    const numB = parseInt(b.replace('FY', ''), 10);
                    return numB - numA;
                });
            for (const key of sortedKeys) {
                const num = parseInt(key.replace('FY', ''), 10);
                const yr = num < 100 ? num : num - 2000;
                if (yr <= currentFY && data[key] && data[key] !== 0) return data[key];
            }
            // Any non-zero value
            const any = Object.values(data).find(v => typeof v === 'number' && v > 0);
            return any || 0;
        };
        
        const authoritativeRevenue = resolveBaseValue(mergedFinancials.revenue);
        const authoritativeEbitda = resolveBaseValue(mergedFinancials.ebitda);
        
        console.log("✅ [Validate] Authoritative base year values:", {
            baseYear: baseYearKey,
            revenue: authoritativeRevenue,
            ebitda: authoritativeEbitda
        });
        
        // ====================================================================
        // STEP 3: Update the base year in the projection model,
        //         respecting user-edited cells
        // ====================================================================
        let updatedModel = JSON.parse(JSON.stringify(model)) as typeof model;
        const updatedScenario = updatedModel.scenarios.find(s => s.id === updatedModel.activeScenarioId);
        
        if (updatedScenario) {
            for (const proj of updatedScenario.projections) {
                if (!proj.isBaseYear) continue;
                
                // --- Revenue base year sync ---
                const currentBaseRev = proj.revenue.total || proj.revenue.businessPlanTarget;
                if (authoritativeRevenue > 0 && Math.abs(currentBaseRev - authoritativeRevenue) > 0.01) {
                    // Only overwrite if the cell was NOT user-edited
                    if (proj.revenue.totalMetadata?.source !== 'user_edited' &&
                        proj.revenue.businessPlanTargetMetadata?.source !== 'user_edited') {
                        validationChanges.push({
                            year: proj.year,
                            field: 'revenue.total',
                            oldValue: currentBaseRev,
                            newValue: authoritativeRevenue,
                            reason: 'Synced from verified financial data in database'
                        });
                        proj.revenue.businessPlanTarget = authoritativeRevenue;
                        proj.revenue.total = authoritativeRevenue;
                        proj.revenue.organicGrowth = 0; // Base year has no organic growth
                    } else {
                        console.log(`[Validate] Skipping base year revenue — user-edited (${currentBaseRev})`);
                    }
                }
                
                // --- EBITDA base year sync ---
                const currentBaseEbitda = proj.ebitda.totalEbitda || proj.ebitda.businessPlanTarget;
                if (authoritativeEbitda > 0 && Math.abs(currentBaseEbitda - authoritativeEbitda) > 0.01) {
                    if (proj.ebitda.totalEbitdaMetadata?.source !== 'user_edited' &&
                        proj.ebitda.businessPlanTargetMetadata?.source !== 'user_edited') {
                        validationChanges.push({
                            year: proj.year,
                            field: 'ebitda.totalEbitda',
                            oldValue: currentBaseEbitda,
                            newValue: authoritativeEbitda,
                            reason: 'Synced from verified financial data in database'
                        });
                        proj.ebitda.businessPlanTarget = authoritativeEbitda;
                        proj.ebitda.totalEbitda = authoritativeEbitda;
                        proj.ebitda.organicGrowth = 0;
                    } else {
                        console.log(`[Validate] Skipping base year EBITDA — user-edited (${currentBaseEbitda})`);
                    }
                }
                
                // --- Base year assumptions: auto-derive from actual data ---
                const actualBaseRevenue = proj.revenue.total;
                const actualBaseEbitda = proj.ebitda.totalEbitda;
                if (actualBaseRevenue > 0 && actualBaseEbitda > 0) {
                    const actualMargin = actualBaseEbitda / actualBaseRevenue;
                    if (proj.assumptions.ebitdaMarginPercentMetadata?.source !== 'user_edited') {
                        const oldMargin = proj.assumptions.ebitdaMarginPercent;
                        if (Math.abs(oldMargin - actualMargin) > 0.001) {
                            validationChanges.push({
                                year: proj.year,
                                field: 'assumptions.ebitdaMarginPercent',
                                oldValue: oldMargin,
                                newValue: actualMargin,
                                reason: `Derived from actual EBITDA (${actualBaseEbitda.toFixed(1)}) / Revenue (${actualBaseRevenue.toFixed(1)})`
                            });
                            proj.assumptions.ebitdaMarginPercent = actualMargin;
                        }
                    }
                }
                
                // --- Base year organic growth rate: derive from prior year ---
                const priorFY = `FY${parseInt(baseYearKey.replace('FY', ''), 10) - 1}`;
                const priorRevenue = mergedFinancials.revenue[priorFY] || 0;
                if (priorRevenue > 0 && actualBaseRevenue > 0) {
                    const actualGrowth = (actualBaseRevenue - priorRevenue) / priorRevenue;
                    if (proj.assumptions.organicGrowthRateMetadata?.source !== 'user_edited') {
                        const oldGrowth = proj.assumptions.organicGrowthRate;
                        if (Math.abs(oldGrowth - actualGrowth) > 0.001) {
                            validationChanges.push({
                                year: proj.year,
                                field: 'assumptions.organicGrowthRate',
                                oldValue: oldGrowth,
                                newValue: actualGrowth,
                                reason: `Derived from actual YoY revenue growth: (${actualBaseRevenue.toFixed(1)} - ${priorRevenue.toFixed(1)}) / ${priorRevenue.toFixed(1)}`
                            });
                            proj.assumptions.organicGrowthRate = actualGrowth;
                        }
                    }
                }
            }
            
            // ================================================================
            // STEP 3b: For projection years, recalibrate Revenue & EBITDA 
            //          BP targets given the now-corrected base year
            // ================================================================
            const oldBaseTotal = model.scenarios.find(s => s.id === model.activeScenarioId)
                ?.projections.find(p => p.isBaseYear)?.revenue.total || 10;
            const baseWasWildlyOff = authoritativeRevenue > 0 && oldBaseTotal > 0 &&
                (authoritativeRevenue / oldBaseTotal > 10 || oldBaseTotal / authoritativeRevenue > 10);
            
            for (const proj of updatedScenario.projections) {
                if (proj.isBaseYear) continue;
                
                // --- Revenue BP Target recalibration ---
                if (proj.revenue.businessPlanTargetMetadata?.source !== 'user_edited' && baseWasWildlyOff) {
                    const oldTarget = proj.revenue.businessPlanTarget;
                    const ratio = oldTarget / oldBaseTotal;
                    const newTarget = authoritativeRevenue * ratio;
                    if (Math.abs(newTarget - oldTarget) > 0.01) {
                        validationChanges.push({
                            year: proj.year,
                            field: 'revenue.businessPlanTarget',
                            oldValue: oldTarget,
                            newValue: newTarget,
                            reason: `Recalibrated from corrected base year (ratio ${ratio.toFixed(2)}x preserved)`
                        });
                        proj.revenue.businessPlanTarget = newTarget;
                    }
                }
                
                // --- EBITDA BP Target: derive from EBITDA margin × Revenue BP target ---
                // If user hasn't manually edited the EBITDA target, recompute it
                if (proj.ebitda.businessPlanTargetMetadata?.source !== 'user_edited') {
                    const ebitdaMargin = proj.assumptions.ebitdaMarginPercent || 0.12;
                    const correctEbitdaTarget = proj.revenue.businessPlanTarget * ebitdaMargin;
                    const oldEbitdaTarget = proj.ebitda.businessPlanTarget;
                    if (Math.abs(correctEbitdaTarget - oldEbitdaTarget) > 0.01) {
                        validationChanges.push({
                            year: proj.year,
                            field: 'ebitda.businessPlanTarget',
                            oldValue: oldEbitdaTarget,
                            newValue: correctEbitdaTarget,
                            reason: `Derived from Revenue BP Target (${proj.revenue.businessPlanTarget.toFixed(1)}) × EBITDA Margin (${(ebitdaMargin * 100).toFixed(1)}%)`
                        });
                        proj.ebitda.businessPlanTarget = correctEbitdaTarget;
                    }
                }
            }
        }
        
        // ====================================================================
        // STEP 4: Recalculate the full model (ensures math consistency)
        // ====================================================================
        const recalculated = recalculateModel(updatedModel);
        
        // ====================================================================
        // STEP 5: Additional sanity checks on the recalculated model
        // ====================================================================
        const recalcScenario = recalculated.scenarios.find(s => s.id === recalculated.activeScenarioId);
        const advisoryIssues: string[] = [];
        
        if (recalcScenario) {
            const projs = recalcScenario.projections;
            for (let i = 1; i < projs.length; i++) {
                const prev = projs[i - 1];
                const curr = projs[i];
                const prevRev = prev.revenue.total || prev.revenue.businessPlanTarget;
                const currRev = curr.revenue.total || curr.revenue.businessPlanTarget;
                if (prevRev > 0 && currRev / prevRev > 3) {
                    advisoryIssues.push(`${curr.year}: Revenue jump >200% YoY (${prevRev.toFixed(1)} → ${currRev.toFixed(1)})`);
                }
                if (curr.assumptions.ebitdaMarginPercent > 0.50) {
                    advisoryIssues.push(`${curr.year}: EBITDA margin ${(curr.assumptions.ebitdaMarginPercent * 100).toFixed(0)}% exceeds industry benchmarks`);
                }
            }
        }
        
        // Attach validation changes and advisory issues to the model
        recalculated.lastValidationChanges = validationChanges;
        recalculated.supervisorValidatedAt = new Date().toISOString();
        recalculated.supervisorIssues = advisoryIssues;
        recalculated.lastUpdatedAt = new Date().toISOString();
        
        // Apply the validated model
        setModel(recalculated);
        
        console.log("✅ [Validate] Model updated with", validationChanges.length, "changes:", validationChanges);
        
        // ====================================================================
        // STEP 6: Push updated base year values to structuredFinancials context
        //         so KPI widgets also see the corrected data
        // ====================================================================
        if (authoritativeRevenue > 0 || authoritativeEbitda > 0) {
            const sfUpdates: Record<string, Record<string, number>> = {};
            if (authoritativeRevenue > 0) {
                sfUpdates.revenue = { ...mergedFinancials.revenue };
            }
            if (authoritativeEbitda > 0) {
                sfUpdates.ebitda = { ...mergedFinancials.ebitda };
            }
            if (Object.keys(mergedFinancials.netProfit).length > 0) {
                sfUpdates.netProfit = { ...mergedFinancials.netProfit };
            }
            updateStructuredFinancials(sfUpdates, true);
            console.log("✅ [Validate] Pushed updated values to structuredFinancials context");
        }
        
        // ====================================================================
        // STEP 7: AI audit (advisory only, non-blocking, with timeout)
        // ====================================================================
        try {
            const trimmedProjections = (recalcScenario?.projections || []).map(p => ({
                year: p.year,
                isBaseYear: p.isBaseYear,
                assumptions: p.assumptions,
                revenue: {
                    businessPlanTarget: p.revenue.businessPlanTarget,
                    organicGrowth: p.revenue.organicGrowth,
                    strategyImpact: p.revenue.strategyImpact,
                    total: p.revenue.total,
                    gapToTarget: p.revenue.gapToTarget,
                    initiativeCount: p.revenue.initiativeBreakdown?.length || 0
                },
                ebitda: {
                    businessPlanTarget: p.ebitda.businessPlanTarget,
                    organicGrowth: p.ebitda.organicGrowth,
                    grossStrategyImpact: p.ebitda.grossStrategyImpact,
                    totalEbitda: p.ebitda.totalEbitda,
                    totalCapex: p.ebitda.totalCapex,
                    totalOpex: p.ebitda.totalOpex,
                    netCashPosition: p.ebitda.netCashPosition,
                    gapToTarget: p.ebitda.gapToTarget
                }
            }));
            
            const minimalModel = {
                ...recalculated,
                scenarios: recalculated.scenarios.map(s =>
                    s.id === recalculated.activeScenarioId
                        ? { ...s, projections: trimmedProjections }
                        : s
                )
            };
            
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);
            
            const response = await fetch('/api/strategy/financial-projection', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'validate',
                    model: minimalModel,
                    companyContext: {
                        name: activeCompany.name || 'Unknown Company',
                        industry: activeCompany.industry || 'Technology',
                        stage: 'Growth',
                        headcount: parseInt(activeCompany.employeeCount) || 50
                    }
                }),
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (response.ok) {
                const result = await response.json();
                if (result.success && result.validatedModel?.supervisorIssues) {
                    // Merge AI advisory issues (don't change values — advisory only)
                    setModel(prev => prev ? {
                        ...prev,
                        supervisorValidationScore: result.validatedModel.supervisorValidationScore ?? prev.supervisorValidationScore,
                        supervisorIssues: [
                            ...(prev.supervisorIssues || []),
                            ...(result.validatedModel.supervisorIssues || [])
                        ].filter((v, i, a) => a.indexOf(v) === i) // deduplicate
                    } : prev);
                }
            } else {
                console.warn(`[Validate] AI advisory returned ${response.status} — local validation stands`);
            }
        } catch (e: any) {
            if (e.name === 'AbortError') {
                console.warn('[Validate] AI advisory timed out — local validation stands');
            } else {
                console.warn('[Validate] AI advisory failed:', e.message, '— local validation stands');
            }
        }
        
        setSupervisorValidationStatus('validated');
    }, [model, activeCompany, structuredFinancials, updateStructuredFinancials]);
    
    // ========================================================================
    // CONTEXT VALUE
    // ========================================================================
    
    const value: FinancialProjectionContextType = {
        model,
        isLoading,
        isCalculating,
        error,
        activeScenario,
        projections,
        yearRange,
        initializeModel,
        updateCell,
        updateAssumption,
        addYear,
        removeYear,
        setYearRange,
        createScenario,
        switchScenario,
        deleteScenario,
        duplicateScenario,
        updateInitiativeImpact,
        syncInitiativeAnalysis,
        collapsedYears,
        toggleYearCollapse,
        expandedInitiatives,
        toggleInitiativeExpand,
        getWaterfallData,
        triggerSupervisorValidation,
        supervisorValidationStatus,
        displayStartYear,
        displayEndYear,
        setDisplayYearRange
    };
    
    return (
        <FinancialProjectionContext.Provider value={value}>
            {children}
        </FinancialProjectionContext.Provider>
    );
};

// ============================================================================
// HOOK
// ============================================================================

export function useFinancialProjection() {
    const context = useContext(FinancialProjectionContext);
    if (!context) {
        throw new Error('useFinancialProjection must be used within a FinancialProjectionProvider');
    }
    return context;
}
